import React from 'react'
import { Icon } from './icons'
import { fmtRelTime } from './format'
import { DataState } from './shared'
import type { ApiError, InstalledMap, InstalledPlugin, PendingPlugin, Role, Server, ServerId } from './types'

interface ApprovalCardProps {
  plugin: PendingPlugin
  role: Role
  servers: Server[]
  onApprove: (plugin: PendingPlugin, target: ServerId, restartAfter: boolean) => void
  onReject: (plugin: PendingPlugin, reason: string) => void
}

export function ApprovalCard({ plugin, role, servers, onApprove, onReject }: ApprovalCardProps) {
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [rejectOpen, setRejectOpen] = React.useState(false)
  const [target, setTarget] = React.useState<ServerId>(servers[0]?.id ?? '')
  const [restartAfter, setRestartAfter] = React.useState(false)
  const [reason, setReason] = React.useState('')
  const canAct = role === 'owner' || role === 'admin'

  return (
    <div className="card" style={{ borderColor: plugin.flagged ? 'color-mix(in oklab, var(--err) 40%, var(--border))' : 'var(--border)', padding: 16 }}>
      <div className="flex items-center gap-3 mb-3">
        <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--surface-2)', display: 'grid', placeItems: 'center', color: plugin.flagged ? 'var(--rose-400)' : 'var(--accent-400)' }}>
          <Icon name={plugin.flagged ? 'alert-triangle' : 'puzzle'} size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mono" style={{ fontWeight: 600, fontSize: 13 }}>{plugin.filename}</div>
          <div className="text-xs dim">
            {plugin.yml.name} <span className="mono">v{plugin.yml.version}</span> - by {plugin.yml.authors.join(', ')}
          </div>
        </div>
        {plugin.flagged && <span className="chip err">flagged</span>}
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <div className="text-xs dim">Uploader</div>
          <div style={{ fontSize: 13 }}>{plugin.uploader}</div>
        </div>
        <div>
          <div className="text-xs dim">Uploaded</div>
          <div style={{ fontSize: 13 }}>{fmtRelTime(plugin.uploadedAt)}</div>
        </div>
        <div>
          <div className="text-xs dim">Size</div>
          <div className="mono" style={{ fontSize: 13 }}>{(plugin.sizeKb / 1024).toFixed(1)} MB</div>
        </div>
        <div>
          <div className="text-xs dim">Main class</div>
          <div className="mono text-xs" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{plugin.yml.main}</div>
        </div>
      </div>

      <div className="text-xs dim mb-1">SHA-256</div>
      <div className="mono text-xs" style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px', marginBottom: 12, wordBreak: 'break-all', color: 'var(--text-muted)' }}>
        {plugin.sha256}
      </div>

      {pickerOpen && (
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 10 }}>
          <div className="text-xs dim mb-2">Install to server</div>
          <div className="seg-radio mb-3">
            {servers.map(server => (
              <button key={server.id} className={target === server.id ? 'active' : ''} onClick={() => setTarget(server.id)}>{server.name}</button>
            ))}
          </div>
          <label className="check">
            <input type="checkbox" checked={restartAfter} onChange={event => setRestartAfter(event.target.checked)} />
            Restart <span className="mono">{target}</span> after install
          </label>
          <div className="flex gap-2 mt-3 justify-end">
            <button className="btn ghost sm" onClick={() => setPickerOpen(false)}>Cancel</button>
            <button className="btn primary sm" onClick={() => { onApprove(plugin, target, restartAfter); setPickerOpen(false) }}>
              <Icon name="check" size={11} /> Install to {target}
            </button>
          </div>
        </div>
      )}

      {rejectOpen && (
        <div style={{ background: 'var(--bg)', border: '1px solid color-mix(in oklab, var(--err) 30%, var(--border))', borderRadius: 6, padding: 12, marginBottom: 10 }}>
          <div className="text-xs dim mb-2">Rejection reason (written to the audit log)</div>
          <textarea rows={2} style={{ width: '100%' }} value={reason} onChange={event => setReason(event.target.value)} placeholder="e.g. unsigned jar, unknown author..." />
          <div className="flex gap-2 mt-3 justify-end">
            <button className="btn ghost sm" onClick={() => setRejectOpen(false)}>Cancel</button>
            <button className="btn danger sm" disabled={!reason.trim()} onClick={() => { onReject(plugin, reason); setRejectOpen(false); setReason('') }}>
              <Icon name="x" size={11} /> Reject
            </button>
          </div>
        </div>
      )}

      {canAct && !pickerOpen && !rejectOpen && (
        <div className="flex gap-2 justify-end">
          <button className="btn ghost" onClick={() => setRejectOpen(true)}><Icon name="x" size={12} /> Reject</button>
          <button className="btn primary" onClick={() => setPickerOpen(true)}>
            <Icon name="check" size={12} /> Approve &amp; install
          </button>
        </div>
      )}
    </div>
  )
}

interface InstalledRowProps {
  plugin: InstalledPlugin
  canAct: boolean
  onToggle: (plugin: InstalledPlugin) => void
  onDelete: (plugin: InstalledPlugin) => void
}

export function InstalledRow({ plugin, canAct, onToggle, onDelete }: InstalledRowProps) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="mono" style={{ fontWeight: 500, fontSize: 13, color: plugin.enabled ? 'var(--text)' : 'var(--text-dim)' }}>{plugin.name}</span>
          <span className="mono dim text-xs">v{plugin.version}</span>
          {!plugin.enabled && <span className="chip">.jar.disabled</span>}
        </div>
        <div className="dim text-xs" style={{ marginTop: 2 }}>by {plugin.authors.join(', ')}</div>
      </div>
      {canAct && (
        <>
          <label className="check" style={{ fontSize: 12 }}>
            <input type="checkbox" checked={plugin.enabled} onChange={() => onToggle(plugin)} />
            {plugin.enabled ? 'enabled' : 'disabled'}
          </label>
          <button className="btn sm ghost" onClick={() => onDelete(plugin)} style={{ color: 'var(--rose-400)' }}>
            <Icon name="trash" size={11} />
          </button>
        </>
      )}
    </div>
  )
}

interface PluginsPageProps {
  role: Role
  pending: PendingPlugin[]
  installed: InstalledMap
  servers: Server[]
  loading: boolean
  error: ApiError | null
  onRetry: () => void
  onApprove: (plugin: PendingPlugin, target: ServerId, restartAfter: boolean) => void
  onReject: (plugin: PendingPlugin, reason: string) => void
  onToggle: (serverId: ServerId, plugin: InstalledPlugin) => void
  onDelete: (serverId: ServerId, plugin: InstalledPlugin) => void
  onUpload: (file: File) => Promise<void>
}

export function PluginsPage({ role, pending, installed, servers, loading, error, onRetry, onApprove, onReject, onToggle, onDelete, onUpload }: PluginsPageProps) {
  const canAct = role === 'owner' || role === 'admin'
  const uploadRef = React.useRef<HTMLInputElement | null>(null)

  if (role === 'player') {
    return (
      <>
        <div className="page-header" data-screen-label="Plugins"><h1>Plugins</h1></div>
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <Icon name="shield" size={26} style={{ opacity: 0.5, marginBottom: 10 }} />
          <h3 style={{ margin: '0 0 4px' }}>403 - Admin only</h3>
          <div className="dim">Players do not manage plugins.</div>
        </div>
      </>
    )
  }

  async function handleUploadChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    await onUpload(file)
    event.target.value = ''
  }

  return (
    <>
      <div className="page-header" data-screen-label="Plugins">
        <h1>Plugins</h1>
        <span className="subtitle">- approval queue + installed plugins across all servers</span>
        <div className="header-right">
          {canAct && <button className="btn primary" onClick={() => uploadRef.current?.click()}><Icon name="upload" size={12} /> Upload plugin</button>}
          <input ref={uploadRef} type="file" accept=".jar" className="hidden" onChange={event => { void handleUploadChange(event) }} />
        </div>
      </div>

      <DataState loading={loading} error={error} onRetry={onRetry}>
        <>
          <div className="flex items-center gap-2 mb-3" style={{ marginTop: 4 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Pending approval</h3>
            <span className="chip warn">{pending.length}</span>
          </div>
          {pending.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 32 }}>
              <Icon name="check-circle" size={22} style={{ opacity: 0.5, marginBottom: 8, color: 'var(--emerald-400)' }} />
              <div>Nothing in the queue.</div>
              <div className="dim text-xs mt-1">New uploads will appear here for review.</div>
            </div>
          ) : (
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
              {pending.map(plugin => (
                <ApprovalCard key={plugin.id ?? plugin.filename} plugin={plugin} role={role} servers={servers} onApprove={onApprove} onReject={onReject} />
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mb-3" style={{ marginTop: 24 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Installed</h3>
          </div>

          <div className="grid" style={{ gridTemplateColumns: `repeat(${Math.max(1, Math.min(servers.length, 2))}, 1fr)`, gap: 14 }}>
            {servers.map(server => {
              const list = installed[server.id] ?? []
              return (
                <div key={server.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Icon name="server" size={14} />
                    <span className="mono" style={{ fontWeight: 600 }}>{server.name}</span>
                    <span className="ml-auto dim text-xs">{list.length} plugin{list.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div>
                    {list.map(plugin => (
                      <InstalledRow key={`${server.id}-${plugin.name}`} plugin={plugin} canAct={canAct} onToggle={item => onToggle(server.id, item)} onDelete={item => onDelete(server.id, item)} />
                    ))}
                    {list.length === 0 && <div className="dim text-sm" style={{ padding: 24, textAlign: 'center' }}>No plugins installed.</div>}
                  </div>
                </div>
              )
            })}
            {servers.length === 0 && <div className="dim text-sm" style={{ padding: 24, textAlign: 'center' }}>No servers configured.</div>}
          </div>
        </>
      </DataState>
    </>
  )
}
